import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { TOKEN_KEY, USER_KEY, REFRESH_KEY, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
    } catch {
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const finalizeLogin = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
    if (data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        return { ok: true, mfa_required: true, mfa_session: data.mfa_session };
      }
      finalizeLogin(data);
      return {
        ok: true,
        mfa_required: false,
        must_change_password: !!data.must_change_password,
      };
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return {
        ok: false,
        error: detail,
        rate_limited: status === 429,
        retry_after: e.response?.headers?.["retry-after"],
      };
    }
  }, [finalizeLogin]);

  const verifyMfa = useCallback(async (mfa_session, code) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_session, code });
      finalizeLogin(data);
      return { ok: true, used_backup_code: data.used_backup_code };
    } catch (e) {
      return {
        ok: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, [finalizeLogin]);

  const adoptSsoTokens = useCallback(async (token, refresh_token) => {
    localStorage.setItem(TOKEN_KEY, token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
    await refreshMe();
  }, [refreshMe]);

  const logout = useCallback(async () => {
    const refresh_token = localStorage.getItem(REFRESH_KEY);
    try {
      await api.post("/auth/logout", { refresh_token: refresh_token || undefined });
    } catch {
      /* best-effort */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(null);
  }, []);

  // Phase 12.1 — let consumers (e.g. CmdK hint) update the cached user shape
  // directly when they have a fresh response from a profile patch endpoint.
  const updateUser = useCallback((next) => {
    if (!next) return;
    setUser(next);
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    verifyMfa,
    adoptSsoTokens,
    logout,
    refreshMe,
    refreshUser: refreshMe,
    setUser: updateUser,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
